package com.bulut.mecitogullari.ui.screens.craftsmen

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.width
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.export.CraftsmanReportData
import com.bulut.mecitogullari.core.export.CraftsmanReportHtml
import com.bulut.mecitogullari.core.platform.isPrintSupported
import com.bulut.mecitogullari.core.platform.printHtml
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.data.model.ApartmentTypes
import com.bulut.mecitogullari.ui.components.AmountField
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel
import kotlin.math.abs
import kotlin.math.roundToLong

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CraftsmenScreen(
    onCraftsmanClick: (craftsmanId: String) -> Unit,
    onBack: () -> Unit,
    viewModel: CraftsmenViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    var showAddDialog by remember { mutableStateOf(false) }
    val strings = LocalStrings.current

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(strings.craftsmenTitle) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    if (isPrintSupported && uiState.craftsmen.isNotEmpty()) {
                        IconButton(
                            onClick = {
                                printHtml(
                                    html = CraftsmanReportHtml.buildAll(
                                        craftsmen = uiState.craftsmen.map {
                                            CraftsmanReportData(it.craftsman, it.balance, it.entries, it.unitsGiven)
                                        },
                                        projectNames = uiState.projectNames,
                                    ),
                                    fileName = "ustalar.pdf",
                                )
                            },
                        ) {
                            Icon(Icons.Default.Share, contentDescription = strings.downloadPdf)
                        }
                    }
                },
            )
        },
        floatingActionButton = {
            ExtendedFloatingActionButton(
                onClick = { showAddDialog = true },
                containerColor = AppColors.Navy,
                contentColor = Color.White,
                icon = { Icon(Icons.Default.Add, contentDescription = null) },
                text = { Text(strings.addCraftsman) },
            )
        },
    ) { padding ->
        if (uiState.craftsmen.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize().padding(padding)) {
                EmptyState(strings.craftsmenEmpty)
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 96.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                items(uiState.craftsmen, key = { it.craftsman.id }) { data ->
                    CraftsmanCard(data = data, onClick = { onCraftsmanClick(data.craftsman.id) })
                }
            }
        }
    }

    if (showAddDialog) {
        AddCraftsmanDialog(
            onDismiss = { showAddDialog = false },
            onCreate = { name, trade, phone, notes, workTypes, invoiceText ->
                viewModel.createCraftsman(name, trade, phone, notes, workTypes, invoiceText)
                showAddDialog = false
            },
        )
    }
}

@Composable
private fun CraftsmanCard(
    data: CraftsmanCardData,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    val craftsman = data.craftsman
    val net = data.balance.currentNet
    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Column(modifier = Modifier.padding(14.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(44.dp)
                        .background(AppColors.Reserved.copy(alpha = 0.16f), RoundedCornerShape(12.dp)),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = craftsman.name.trim().take(1).uppercase(),
                        style = MaterialTheme.typography.titleMedium,
                        color = AppColors.Reserved,
                        fontWeight = FontWeight.Bold,
                    )
                }
                Column(modifier = Modifier.weight(1f).padding(horizontal = 12.dp)) {
                    Text(
                        text = craftsman.name,
                        style = MaterialTheme.typography.titleSmall,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    Text(
                        text = buildString {
                            if (craftsman.trade.isNotBlank()) append(craftsman.trade)
                            if (data.unitsGivenCount > 0) {
                                if (isNotEmpty()) append("  •  ")
                                append(
                                    strings.unitsGivenCount
                                        .fillTemplate("count" to data.unitsGivenCount.toString())
                                )
                            }
                        }.ifBlank { strings.craftsman },
                        style = MaterialTheme.typography.labelSmall,
                        color = AppColors.TextSecondary,
                    )
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        text = formatAmount(abs(net)),
                        style = MaterialTheme.typography.titleSmall,
                        color = when {
                            net > 0 -> AppColors.Expense
                            net < 0 -> AppColors.Income
                            else -> AppColors.TextSecondary
                        },
                    )
                    Text(
                        text = when {
                            net > 0 -> strings.craftsmanOwed
                            net < 0 -> strings.craftsmanOwes
                            else -> strings.balanced
                        },
                        style = MaterialTheme.typography.labelSmall,
                        color = AppColors.TextSecondary,
                    )
                }
            }

            if (craftsman.totalAgreedCount > 0) {
                val progress = (craftsman.totalCompletedCount / craftsman.totalAgreedCount)
                    .toFloat().coerceIn(0f, 1f)
                Row(
                    modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Text(
                        text = strings.workDone,
                        style = MaterialTheme.typography.labelSmall,
                        color = AppColors.TextSecondary,
                    )
                    Text(
                        text = "${formatWork(craftsman.totalCompletedCount)}/" +
                            "${formatWork(craftsman.totalAgreedCount)} ${strings.units}",
                        style = MaterialTheme.typography.labelSmall,
                        color = AppColors.TextPrimary,
                    )
                }
                LinearProgressIndicator(
                    progress = { progress },
                    color = if (progress >= 1f) AppColors.Income else AppColors.Reserved,
                    trackColor = AppColors.Outline,
                    modifier = Modifier.fillMaxWidth().padding(top = 5.dp).height(6.dp),
                )
                Text(
                    text = craftsman.effectiveWorkTypes
                        .filter { it.agreedCount > 0 }
                        .joinToString("   ") {
                            "${it.type}: ${formatWork(it.completedCount)}/${formatWork(it.agreedCount)}"
                        },
                    style = MaterialTheme.typography.labelSmall,
                    color = AppColors.TextSecondary,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            if (data.balance.requiredInvoice > 0) {
                Text(
                    text = strings.invoiceProgress.fillTemplate(
                        "invoiced" to formatAmount(data.balance.invoiced),
                        "required" to formatAmount(data.balance.requiredInvoice),
                    ),
                    style = MaterialTheme.typography.labelSmall,
                    color = if (data.balance.remainingInvoice > 0) AppColors.Reserved else AppColors.Income,
                    modifier = Modifier.padding(top = 6.dp),
                )
            }
        }
    }
}

internal fun formatWork(value: Double): String =
    if (value == value.roundToLong().toDouble()) {
        value.roundToLong().toString()
    } else {
        value.toString().replace('.', ',')
    }

@Composable
private fun AddCraftsmanDialog(
    onDismiss: () -> Unit,
    onCreate: (
        name: String,
        trade: String,
        phone: String,
        notes: String,
        workTypes: List<WorkTypeInput>,
        invoiceText: String,
    ) -> Unit,
) {
    var name by remember { mutableStateOf("") }
    var trade by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var notes by remember { mutableStateOf("") }
    var invoiceText by remember { mutableStateOf("") }
    val strings = LocalStrings.current
    val workTypes = remember {
        mutableStateListOf(*ApartmentTypes.map { WorkTypeInput(type = it) }.toTypedArray())
    }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Text(strings.newCraftsman, style = MaterialTheme.typography.titleMedium)
                Text(
                    text = strings.newCraftsmanIntro,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )

                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text(strings.craftsmanNameLabel) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = trade,
                    onValueChange = { trade = it },
                    label = { Text(strings.tradeLabel) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )
                OutlinedTextField(
                    value = phone,
                    onValueChange = { phone = it },
                    label = { Text(strings.phoneOptional) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )
                Text(
                    text = strings.agreedWorkSection,
                    style = MaterialTheme.typography.labelLarge,
                    color = AppColors.TextSecondary,
                )
                workTypes.forEachIndexed { index, input ->
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = input.type,
                            style = MaterialTheme.typography.titleSmall,
                            modifier = Modifier.width(36.dp),
                        )
                        OutlinedTextField(
                            value = input.countText,
                            onValueChange = { value ->
                                workTypes[index] = input.copy(
                                    countText = value.filter { c -> c.isDigit() || c == ',' },
                                )
                            },
                            label = { Text(strings.countLabel) },
                            singleLine = true,
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.weight(0.8f),
                        )
                        AmountField(
                            label = strings.pricePerUnitLabel,
                            value = input.priceText,
                            onValueChange = { workTypes[index] = input.copy(priceText = it) },
                            modifier = Modifier.weight(1.2f),
                        )
                    }
                }
                AmountField(
                    label = strings.requiredInvoiceLabel,
                    value = invoiceText,
                    onValueChange = { invoiceText = it },
                    modifier = Modifier.fillMaxWidth(),
                )
                Text(
                    text = strings.requiredInvoiceHint,
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                )
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text(strings.noteOptional) },
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = { onCreate(name, trade, phone, notes, workTypes.toList(), invoiceText) },
                        enabled = name.isNotBlank(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.add, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}
